/**
 * Union signed distance to a set of closed(-ish) mesh parts.
 *
 * sd_union(p) = min over parts of the signed distance to that part, the
 * standard CSG-union field. Signs come from angle-weighted pseudo-normals
 * (Bærentzen & Aanæs): closest point → closest feature (face / edge / vertex)
 * → dot with that feature's pseudo-normal — provably the correct side for a
 * closed mesh even when the closest point lands exactly on an edge or vertex,
 * where a plain face normal degenerates into salt-and-pepper sign noise.
 *
 * Shared between the main thread (SDFGenerator) and the SDF workers, so the
 * pseudo-normals use transfer-friendly flat arrays (no Maps).
 */

import { Vector3 } from 'three';
import type { MeshBVH } from 'three-mesh-bvh';

/** Angle-weighted pseudo-normals over position-welded topology (flat arrays). */
export type PseudoNormals = {
  /** Welded vertex id per position entry. */
  weldOf: Int32Array;
  weldedCount: number;
  /** Unit normal per face (face order = the geometry's index order). */
  faceNormals: Float32Array;
  /** Angle-weighted accumulated normal per welded vertex. */
  vertexNormals: Float32Array;
  /** Sorted welded-edge keys (min·weldedCount+max) for binary search. */
  edgeKeys: Float64Array;
  /** Accumulated normal per edge, aligned with edgeKeys. */
  edgeNormals: Float32Array;
  /** 1 = open-border edge (single adjacent face), aligned with edgeKeys. */
  borderEdge: Uint8Array;
  /** 1 = welded vertex lies on an open border. */
  borderVertex: Uint8Array;
};

/** One closed part ready for signed closest-point queries. */
export type SignedPart = {
  bvh: MeshBVH;
  positions: Float32Array;
  /** The geometry's index (BVH-reordered); null for pure soup queries. */
  index: ArrayLike<number> | null;
  pn: PseudoNormals;
  boxMin: { x: number; y: number; z: number };
  boxMax: { x: number; y: number; z: number };
};

export type UnionQueryScratch = {
  hit: { point: Vector3; distance: number; faceIndex: number };
  a: Vector3;
  b: Vector3;
  c: Vector3;
  normal: Vector3;
  delta: Vector3;
  seg: Vector3;
  toC: Vector3;
  point: Vector3;
};

export function createUnionQueryScratch(): UnionQueryScratch {
  return {
    hit: { point: new Vector3(), distance: 0, faceIndex: 0 },
    a: new Vector3(),
    b: new Vector3(),
    c: new Vector3(),
    normal: new Vector3(),
    delta: new Vector3(),
    seg: new Vector3(),
    toC: new Vector3(),
    point: new Vector3(),
  };
}

/**
 * Builds pseudo-normals for a (possibly non-indexed soup) triangle list.
 * MUST be called with the index the BVH queries report faceIndex against —
 * i.e. after the MeshBVH was built/deserialized for this geometry.
 */
export function buildPseudoNormals(
  positions: Float32Array,
  index: ArrayLike<number> | null,
): PseudoNormals {
  const vertexCount = positions.length / 3;
  const cornerCount = index ? index.length : vertexCount;
  const triCount = cornerCount / 3;

  const weldOf = new Int32Array(vertexCount);
  const weldMap = new Map<string, number>();
  for (let i = 0; i < vertexCount; i++) {
    const key =
      `${Math.round(positions[i * 3] * 1e4)},` +
      `${Math.round(positions[i * 3 + 1] * 1e4)},` +
      `${Math.round(positions[i * 3 + 2] * 1e4)}`;
    let id = weldMap.get(key);
    if (id === undefined) {
      id = weldMap.size;
      weldMap.set(key, id);
    }
    weldOf[i] = id;
  }
  const weldedCount = weldMap.size;

  const faceNormals = new Float32Array(triCount * 3);
  const vertexNormals = new Float32Array(weldedCount * 3);
  const edgeSlot = new Map<number, number>();
  const edgeAccum: number[] = [];
  const a = new Vector3();
  const b = new Vector3();
  const c = new Vector3();
  const ab = new Vector3();
  const ac = new Vector3();
  const n = new Vector3();
  const cornerOf = (t: number, k: number) => (index ? index[t * 3 + k] : t * 3 + k);
  const angleAt = (apex: Vector3, p: Vector3, q: Vector3) => {
    ab.subVectors(p, apex).normalize();
    ac.subVectors(q, apex).normalize();
    return Math.acos(Math.min(1, Math.max(-1, ab.dot(ac))));
  };
  const edgeFaces: number[] = [];
  const accumulateEdge = (u: number, v: number) => {
    const key = u < v ? u * weldedCount + v : v * weldedCount + u;
    let slot = edgeSlot.get(key);
    if (slot === undefined) {
      slot = edgeAccum.length;
      edgeSlot.set(key, slot);
      edgeAccum.push(0, 0, 0);
      edgeFaces.push(0);
    }
    edgeAccum[slot] += n.x;
    edgeAccum[slot + 1] += n.y;
    edgeAccum[slot + 2] += n.z;
    edgeFaces[slot / 3]++;
  };

  for (let t = 0; t < triCount; t++) {
    const ia = cornerOf(t, 0);
    const ib = cornerOf(t, 1);
    const ic = cornerOf(t, 2);
    a.fromArray(positions, ia * 3);
    b.fromArray(positions, ib * 3);
    c.fromArray(positions, ic * 3);
    n.crossVectors(ab.subVectors(b, a), ac.subVectors(c, a));
    const len = n.length();
    if (len < 1e-12) continue;
    n.divideScalar(len);
    faceNormals[t * 3] = n.x;
    faceNormals[t * 3 + 1] = n.y;
    faceNormals[t * 3 + 2] = n.z;

    const wa = weldOf[ia];
    const wb = weldOf[ib];
    const wc = weldOf[ic];
    const addVertex = (w: number, angle: number) => {
      vertexNormals[w * 3] += n.x * angle;
      vertexNormals[w * 3 + 1] += n.y * angle;
      vertexNormals[w * 3 + 2] += n.z * angle;
    };
    addVertex(wa, angleAt(a, b, c));
    addVertex(wb, angleAt(b, c, a));
    addVertex(wc, angleAt(c, a, b));
    accumulateEdge(wa, wb);
    accumulateEdge(wb, wc);
    accumulateEdge(wc, wa);
  }

  // Flatten the edge map into sorted parallel arrays (transferable, and binary
  // search beats a 2M-entry Map for both clone cost and lookup speed).
  const edgeCount = edgeSlot.size;
  const keyList = new Float64Array(edgeCount);
  const slotList = new Int32Array(edgeCount);
  let e = 0;
  edgeSlot.forEach((slot, key) => {
    keyList[e] = key;
    slotList[e] = slot;
    e++;
  });
  const order = Array.from({ length: edgeCount }, (_, i) => i).sort(
    (i, j) => keyList[i] - keyList[j],
  );
  const edgeKeys = new Float64Array(edgeCount);
  const edgeNormals = new Float32Array(edgeCount * 3);
  const borderEdge = new Uint8Array(edgeCount);
  const borderVertex = new Uint8Array(weldedCount);
  for (let i = 0; i < edgeCount; i++) {
    const src = order[i];
    const key = keyList[src];
    edgeKeys[i] = key;
    const slot = slotList[src];
    edgeNormals[i * 3] = edgeAccum[slot];
    edgeNormals[i * 3 + 1] = edgeAccum[slot + 1];
    edgeNormals[i * 3 + 2] = edgeAccum[slot + 2];
    if (edgeFaces[slot / 3] === 1) {
      // Open-border edge: its pseudo-normal comes from a single face, so the
      // side verdict it gives is unreliable — flag it and its endpoints.
      borderEdge[i] = 1;
      const u = Math.floor(key / weldedCount);
      const v = key - u * weldedCount;
      borderVertex[u] = 1;
      borderVertex[v] = 1;
    }
  }
  return {
    weldOf,
    weldedCount,
    faceNormals,
    vertexNormals,
    edgeKeys,
    edgeNormals,
    borderEdge,
    borderVertex,
  };
}

/** Binary search for the welded edge (u, v); returns its index or −1. */
function edgeLookup(pn: PseudoNormals, u: number, v: number): number {
  const key = u < v ? u * pn.weldedCount + v : v * pn.weldedCount + u;
  let lo = 0;
  let hi = pn.edgeKeys.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const k = pn.edgeKeys[mid];
    if (k === key) return mid;
    if (k < key) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
}

const FEATURE_EPS_SQ = 1e-10;
const COS_AMBIGUOUS = 0.15;

/**
 * Signed distance from `scratch.point` to one part, or null when nothing lies
 * within `maxDist`. `ambiguous` marks a near-tangential closest feature whose
 * side is not trustworthy.
 */
export function signedDistanceToPart(
  part: SignedPart,
  maxDist: number,
  scratch: UnionQueryScratch,
): { sd: number; ambiguous: boolean } | null {
  const { hit, a, b, c, normal, delta, seg, toC, point } = scratch;
  const found = part.bvh.closestPointToPoint(
    point,
    hit as Parameters<MeshBVH['closestPointToPoint']>[1],
    0,
    maxDist,
  );
  if (!found) return null;
  if (hit.distance < 1e-6) return { sd: 0, ambiguous: false };

  const pn = part.pn;
  const closest = hit.point;
  const t = hit.faceIndex;
  const idx = part.index;
  const ia = idx ? idx[t * 3] : t * 3;
  const ib = idx ? idx[t * 3 + 1] : t * 3 + 1;
  const ic = idx ? idx[t * 3 + 2] : t * 3 + 2;
  a.fromArray(part.positions, ia * 3);
  b.fromArray(part.positions, ib * 3);
  c.fromArray(part.positions, ic * 3);

  const onSegment = (p: Vector3, q: Vector3): boolean => {
    seg.subVectors(q, p);
    const lenSq = seg.lengthSq();
    if (lenSq < 1e-20) return false;
    toC.subVectors(closest, p);
    const s = Math.min(1, Math.max(0, toC.dot(seg) / lenSq));
    return toC.addScaledVector(seg, -s).lengthSq() < FEATURE_EPS_SQ;
  };

  // Features on an OPEN border (rim of a sheet) see the surface from one side
  // only, so they have no trustworthy side verdict at all. Report them as
  // OUTSIDE: the part's slab then ends in a flush cut at its rim instead of
  // wrapping the free edge in a wall-thick half-tube (the "pearl chain" bumps
  // along the gum line and interproximal seams — a 0.5 mm bead that no
  // realistic rim tuck can bury). Rims that meet another part are sealed by
  // that part's own slab (rim tucking guarantees the sheets cross there);
  // rims far from everything get a clean square end.
  normal.set(0, 0, 0);
  if (closest.distanceToSquared(a) < FEATURE_EPS_SQ) {
    if (pn.borderVertex[pn.weldOf[ia]]) return { sd: hit.distance, ambiguous: false };
    normal.fromArray(pn.vertexNormals, pn.weldOf[ia] * 3);
  } else if (closest.distanceToSquared(b) < FEATURE_EPS_SQ) {
    if (pn.borderVertex[pn.weldOf[ib]]) return { sd: hit.distance, ambiguous: false };
    normal.fromArray(pn.vertexNormals, pn.weldOf[ib] * 3);
  } else if (closest.distanceToSquared(c) < FEATURE_EPS_SQ) {
    if (pn.borderVertex[pn.weldOf[ic]]) return { sd: hit.distance, ambiguous: false };
    normal.fromArray(pn.vertexNormals, pn.weldOf[ic] * 3);
  } else {
    let edgeIndex = -1;
    if (onSegment(a, b)) edgeIndex = edgeLookup(pn, pn.weldOf[ia], pn.weldOf[ib]);
    else if (onSegment(b, c)) edgeIndex = edgeLookup(pn, pn.weldOf[ib], pn.weldOf[ic]);
    else if (onSegment(c, a)) edgeIndex = edgeLookup(pn, pn.weldOf[ic], pn.weldOf[ia]);
    if (edgeIndex >= 0) {
      if (pn.borderEdge[edgeIndex]) return { sd: hit.distance, ambiguous: false };
      normal.set(
        pn.edgeNormals[edgeIndex * 3],
        pn.edgeNormals[edgeIndex * 3 + 1],
        pn.edgeNormals[edgeIndex * 3 + 2],
      );
    }
    if (normal.lengthSq() < 1e-12) normal.fromArray(pn.faceNormals, t * 3);
  }

  const normalLen = normal.length();
  if (normalLen < 1e-12) return { sd: hit.distance, ambiguous: true };
  delta.subVectors(point, closest);
  const cos = delta.dot(normal) / (normalLen * hit.distance);
  if (Math.abs(cos) < COS_AMBIGUOUS) return { sd: hit.distance, ambiguous: true };
  return { sd: cos > 0 ? hit.distance : -hit.distance, ambiguous: false };
}

/**
 * Union signed distance at `scratch.point`: min over parts, with bbox pruning
 * (a part can only deepen the union when it CONTAINS the point, and only beat
 * a positive best when its box is nearer than that best).
 *
 * With `slabMm > 0` each part is thickened into a slab of that depth behind
 * its surface BEFORE the min: sd_i → max(sd_i, −sd_i − slabMm). This is the
 * correct union for OPEN sheets (scan bands, crown shells): their raw signed
 * distance keeps growing "behind" the sheet without bound, so a point shallow
 * behind one part but deep behind another gets poisoned by the deep value —
 * min(−0.3, −2.0) = −2.0 — and the shell transform later reads that as
 * hollow interior, shredding the wall into a solid/empty checkerboard
 * wherever sheets overlap. Slabbing first also silences the far-field sign
 * chaos of folded sheets: whichever side a distant fold verdict lands on,
 * the slab value stays positive and never crosses zero.
 */
export function unionSignedDistance(
  parts: SignedPart[],
  scratch: UnionQueryScratch,
  slabMm = 0,
): { sd: number; ambiguous: boolean } {
  const point = scratch.point;
  const floor = slabMm > 0 ? -slabMm / 2 : -Infinity;
  let best = Infinity;
  let bestAmbiguous = false;
  for (const part of parts) {
    if (best <= floor) break; // a slab field can never go deeper than −slab/2
    const dx = Math.max(part.boxMin.x - point.x, 0, point.x - part.boxMax.x);
    const dy = Math.max(part.boxMin.y - point.y, 0, point.y - part.boxMax.y);
    const dz = Math.max(part.boxMin.z - point.z, 0, point.z - part.boxMax.z);
    const boxSq = dx * dx + dy * dy + dz * dz;
    // In slab mode a part whose surface is up to slabMm FARTHER than `best`
    // can still win with its behind-the-sheet value (d − slab < best), so the
    // pruning reach grows by slabMm.
    const reach = best > 0 && Number.isFinite(best) ? best + slabMm : Infinity;
    if (best <= 0) {
      if (slabMm > 0 ? boxSq >= slabMm * slabMm : boxSq > 0) continue;
    } else if (boxSq >= reach * reach) {
      continue;
    }
    const result = signedDistanceToPart(part, reach, scratch);
    if (!result) continue;
    const sd = slabMm > 0 ? Math.max(result.sd, -result.sd - slabMm) : result.sd;
    if (sd < best) {
      best = sd;
      bestAmbiguous = result.ambiguous;
    }
  }
  return { sd: best, ambiguous: bestAmbiguous };
}
